import { Suspense, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../store/authStore";
import { useNotificationStore } from "../../store/notificationStore";
import { useDockAvoidStore } from "../../store/dockAvoidStore";
import SideNav from "./SideNav";
import NotificationToast from "../notification/NotificationToast";
import ErrorToast from "../notification/ErrorToast";
import { getUserChannel } from "../../api/user";
import { onRabbitChannelEvent } from "../../hooks/useRabbitMQ";
import { bootstrapPubNub } from "../../services/pubnubBootstrap";
import {
  registerPushIfEnabled,
  onForegroundMessage,
} from "../../lib/notifications";
import { usePushStore } from "../../store/pushStore";
import i18n from "../../i18n";
import type { RemoteNoti } from "../../api/notifications";
import logger from "../../lib/logger";
import { rememberReturnToUnlessSignedOut } from "../../lib/returnTo";
import ConsentGate from "../consent/ConsentGate";
import { useConsentStore } from "../../store/consentStore";
import { destroyPubNub } from "../../hooks/usePubNub";
import UpdatedNoticeBanner from "../consent/UpdatedNoticeBanner";
import RouteFallback from "./RouteFallback";

export default function ProtectedLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const hasInformation = useAuthStore((s) => s.hasInformation);
  const setChannel = useAuthStore((s) => s.setChannel);
  const userId = useAuthStore((s) => s.userId);
  const dispatch = useNotificationStore((s) => s.dispatch);
  const dispatchAppNoti = useNotificationStore((s) => s.dispatchAppNoti);
  const { pathname, search, hash } = useLocation();
  // Phone: the dock's mini player (or the dock moved to the bottom) over the screen's foot — the content pads its end by
  // its reach, so list ends and the map's sheets clear it (StreamDock publishes it; 0 on desktop).
  const dockInset = useDockAvoidStore((s) => s.bottomInset);
  const queryClient = useQueryClient();
  // Privacy consent gate: while the account still owes its date of birth or a consent answer, nothing connects —
  // no RabbitMQ queue, no PubNub, no push registration, no foreground messages. They start once both are answered
  // and stop again if a pending flag appears (e.g. a refresh reports a new material version).
  const gated = useConsentStore((s) => s.consentPending || s.birthDatePending);
  const servicesOn = isAuthenticated && !gated;

  const { data: channel } = useQuery({
    queryKey: ["userChannel"],
    queryFn: getUserChannel,
    enabled: servicesOn,
    staleTime: Infinity,
  });

  useEffect(
    () =>
      onRabbitChannelEvent((e) => {
        if (e === "queueMissing") {
          queryClient.invalidateQueries({ queryKey: ["userChannel"] });
        } else {
          // Missed events while disconnected: refresh whatever is on screen.
          queryClient.invalidateQueries();
        }
      }),
    [queryClient],
  );

  useEffect(() => {
    // No channel while gated: KeepaliveHost's RabbitMQ subscription follows authStore.channel and closes.
    if (!servicesOn) setChannel(null);
    else if (channel) setChannel(channel);
  }, [channel, setChannel, servicesOn]);

  useEffect(() => {
    if (servicesOn) {
      bootstrapPubNub().catch((e) => logger.warn("PubNub bootstrap failed", e));
    } else if (isAuthenticated) {
      destroyPubNub();
    }
  }, [servicesOn, isAuthenticated]);

  // Push permission is asked only on a user tap (NotificationPrePrompt). On load a browser whose permission is already
  // granted registers / refreshes its token as before — never a permission prompt here.
  const pushEnabled = usePushStore((s) => s.enabled);
  useEffect(() => {
    if (servicesOn && userId && pushEnabled) {
      registerPushIfEnabled(userId).catch((e) =>
        logger.warn("Push registration failed", e),
      );
    }
  }, [servicesOn, userId, pushEnabled]);

  useEffect(() => {
    if (!servicesOn || !pushEnabled) return;
    return onForegroundMessage((payload) => {
      // Android MyFcmService routes by data["service"]:
      // "whoisit" → normal notification (badge bump), "pubnub" → chat message
      const data = payload.data as Record<string, string> | undefined;
      if (data?.service === "whoisit") {
        let noti: RemoteNoti | null = null;
        try {
          noti = JSON.parse(data.content ?? "{}");
        } catch {
          /* unparseable content still bumps the badge */
        }
        if (noti?.visible === false) return;
        queryClient.invalidateQueries({ queryKey: ["notificationsBadge"] });
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
        if (noti) dispatchAppNoti(noti, pathname, i18n.language, data.deeplink);
        return;
      }
      dispatch(payload, pathname);
    });
  }, [servicesOn, pushEnabled, pathname, dispatch, dispatchAppNoti, queryClient]);

  const redirecting = !isAuthenticated || hasInformation === false;

  // Both redirects remember the opened link so sign-in (and the profile step) can continue to it,
  // except right after an explicit sign-out (the marker is spent here).
  useEffect(() => {
    if (redirecting) rememberReturnToUnlessSignedOut(pathname + search + hash);
  }, [redirecting, pathname, search, hash]);

  if (redirecting) {
    return <Navigate to={isAuthenticated ? "/first-login" : "/login"} replace />;
  }
  // Blocking gate (privacy consent): Date of birth / Before you start / re-consent before any app screen.
  return (
    <ConsentGate>
      <div className="flex flex-row h-full">
        <SideNav />
        <main
          className="flex-1 overflow-y-auto min-w-0 pb-14 md:pb-0"
          style={dockInset > 0 ? { paddingBottom: `calc(3.5rem + ${dockInset}px)` } : undefined}
        >
          <UpdatedNoticeBanner />
          {/* Its own boundary: a lazy page suspending here shows the spinner in the page, instead of App's
              boundary keeping the answered consent gate on screen (frozen) until the chunk arrives. */}
          <Suspense fallback={<RouteFallback inPage />}>
            <Outlet />
          </Suspense>
        </main>
        <NotificationToast />
        <ErrorToast />
      </div>
    </ConsentGate>
  );
}
